import styled from "@emotion/styled";
import { Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Bank, Money } from "@phosphor-icons/react";
import { useChurchStore } from "features/churches";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useBalanceStatus,
  useExpenseStatus,
  useRevenueStatus,
  useTeamBudget,
  useTopBanks
} from "../api";
import { getChartLabels, getChartValues } from "../utils";
import { SectionBar } from "./SectionBar";
import { Toolbar } from "./Toolbar";
import { LineChart } from "./charts/LineChart";
import { PieChart } from "./charts/PieChart";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Section = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 22px;
`;

const SectionTitle = styled.h4`
  margin: 0;
  margin-bottom: 24px;
`;

const FlatCard = styled.div`
  background: #f3f4f6;
  border-radius: 6px;
  flex: 1;
  border-left: 22px solid #7fa457;
  padding: 11px;
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendIndicator = styled.div`
  width: 24px;
  height: 24px;
  background: ${({ color }) => color};
`;

const Graph = styled.div`
  margin-top: 40px;
  flex-basis: 70%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const LegendAside = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LegendBox = styled.div`
  min-width: 200px;
  max-width: 200px;
  min-height: 70px;
  border-radius: 4px;
  padding: 18px 14px;
  background-color: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #ffffff;

  h2,
  h4 {
    margin: 0;
    padding: 0;
  }
`;

const GraphFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const ChartContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  width: 100%;
  gap: 48px;
`;

const ChartSection = styled.div`
  flex: 0 0 auto;
  min-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const LegendContainer = styled.div`
  flex: 1 1 220px;
  max-width: 300px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledLegendItem = styled.div`
  display: flex;
  align-items: center;
  background: #f6f7f9;
  border-radius: 8px;
  min-width: 220px;
  border-left: 8px solid ${({ color }) => color};
  padding: 10px 18px;
`;

const LegendPercent = styled.span`
  font-weight: bold;
  font-size: 1.1rem;
  margin-right: 8px;
`;

const LegendLabel = styled.span`
  font-size: 1.05rem;
`;

export const FinancesView = () => {
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const [labels, setLabels] = useState([]);
  const [selectedQueryPeriod, setSelectedQueryPeriod] = useState(null);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalExpense, setTotalExpense] = useState(0);
  const [revenueData, setRevenueData] = useState([]);
  const [expenseData, setExpenseData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [revenuePieChartData, setRevenuePieChartData] = useState([]);
  const [expensePieChartData, setExpensePieChartData] = useState([]);

  const { t } = useTranslation();

  useBalanceStatus({
    churchId: selectedChurch?.id,
    fromDate: selectedQueryPeriod?.fromDate,
    toDate: selectedQueryPeriod?.toDate,
    period: selectedQueryPeriod?.period,
    config: {
      enabled: !!selectedQueryPeriod?.toDate,
      onSuccess: response => {
        if (response) {
          setLabels(getChartLabels(response.data));
          setRevenueData(
            getChartValues(response.data).map(item => item.revenue)
          );
          setExpenseData(
            getChartValues(response.data).map(item => item.expense)
          );
          setTotalRevenue(response.total.total_revenue);
          setTotalExpense(response.total.total_expense);
        }
      }
    }
  });

  useRevenueStatus({
    churchId: selectedChurch?.id,
    fromDate: selectedQueryPeriod?.fromDate,
    toDate: selectedQueryPeriod?.toDate,
    period: selectedQueryPeriod?.period,
    config: {
      enabled: !!selectedQueryPeriod?.toDate,
      onSuccess: response => {
        if (response) {
          setRevenuePieChartData(
            response.data.map(item => ({
              name: item.label,
              value: item.value,
              color: generateRandomColor()
            }))
          );
        }
      }
    }
  });

  useExpenseStatus({
    churchId: selectedChurch?.id,
    fromDate: selectedQueryPeriod?.fromDate,
    toDate: selectedQueryPeriod?.toDate,
    period: selectedQueryPeriod?.period,
    config: {
      enabled: !!selectedQueryPeriod?.toDate,
      onSuccess: response => {
        if (response) {
          setExpensePieChartData(
            response.data.map(item => ({
              name: item.label,
              value: item.value,
              color: generateRandomColor()
            }))
          );
        }
      }
    }
  });

  const teamBudgetQuery = useTeamBudget({
    churchId: selectedChurch?.id,
    config: {
      enabled: !!selectedChurch?.id
    }
  });

  const topBanksQuery = useTopBanks({
    churchId: selectedChurch?.id,
    config: {
      enabled: !!selectedChurch?.id
    }
  });

  const handlePeriod = selectedPeriod => {
    setSelectedQueryPeriod(selectedPeriod);
  };

  const generateRandomColor = () => {
    const randomColorValue = Math.floor(Math.random() * 0xffffff);
    let hexColor = randomColorValue.toString(16);
    while (hexColor.length < 6) {
      hexColor = "0" + hexColor;
    }
    return "#" + hexColor;
  };

  return (
    <Container>
      <SectionBar
        icon={<Money size={24} />}
        title={t("dashboard.finance.financesData")}
        color="#7FA457"
      />

      {/* Top Banks */}
      <Section>
        <SectionTitle>
          {t("dashboard.finance.churchBalancePerBank")}
        </SectionTitle>
        <Stack direction="row" alignItems="center" spacing={3}>
          {topBanksQuery.data?.top_banks?.map(bank => (
            <FlatCard key={bank.id}>
              <Stack spacing={1}>
                <Stack
                  sx={{
                    alignSelf: "flex-end",
                    background: "white",
                    borderRadius: "4px",
                    p: 1,
                    color: "#7FA457"
                  }}
                >
                  <Bank size={24} />
                </Stack>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {bank.bank_name}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "#7FA457" }}
                >
                  {bank.balance.toLocaleString()}
                </Typography>
              </Stack>
            </FlatCard>
          ))}
        </Stack>
      </Section>

      {/* Balance Status */}
      <Section>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <SectionTitle>{t("dashboard.finance.balanceStatus")}</SectionTitle>
          <Toolbar onChange={handlePeriod} />
        </Stack>
        <Stack direction="row" alignItems="center" gap={8}>
          <Graph>
            <LineChart
              labels={labels}
              data={[
                {
                  name: t("dashboard.finance.revenue"),
                  data: revenueData
                },
                {
                  name: t("dashboard.finance.expense"),
                  data: expenseData
                }
              ]}
            />
            <GraphFooter>
              <Legend>
                <LegendIndicator color="#FBD8B0" />
                <h5>{t("dashboard.finance.expense")}</h5>
              </Legend>
              <Legend>
                <LegendIndicator color="#74DFD5" />
                <h5>{t("dashboard.finance.revenue")}</h5>
              </Legend>
            </GraphFooter>
          </Graph>
          <LegendAside>
            <LegendBox color="#74DFD5">
              <h4>{t("dashboard.finance.revenue")}</h4>
              <h2>{totalRevenue.toLocaleString()}</h2>
            </LegendBox>
            <LegendBox color="#FBD8B0">
              <h4>{t("dashboard.finance.expense")}</h4>
              <h2>{totalExpense.toLocaleString()}</h2>
            </LegendBox>
          </LegendAside>
        </Stack>
      </Section>

      {/* Revenue Status */}
      <Section>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <SectionTitle>{t("dashboard.finance.revenueStatus")}</SectionTitle>
          <Toolbar onChange={handlePeriod} />
        </Stack>
        <ChartContainer>
          <LegendContainer>
            {revenuePieChartData.map(item => (
              <StyledLegendItem key={item.name} color={item.color}>
                <LegendPercent>{item.value} %</LegendPercent>
                <LegendLabel>{item.name}</LegendLabel>
              </StyledLegendItem>
            ))}
          </LegendContainer>
          <ChartSection>
            <PieChart
              data={revenuePieChartData}
              colors={revenuePieChartData.map(item => item.color)}
              showLegend={false}
              height={400}
              width={400}
            />
          </ChartSection>
        </ChartContainer>
      </Section>

      {/* Expense Status */}
      <Section>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <SectionTitle>{t("dashboard.finance.expenseStatus")}</SectionTitle>
          <Toolbar onChange={handlePeriod} />
        </Stack>
        <ChartContainer>
          <ChartSection>
            <PieChart
              data={expensePieChartData}
              colors={expensePieChartData.map(item => item.color)}
              showLegend={false}
              height={400}
              width={400}
            />
          </ChartSection>
          <LegendContainer>
            {expensePieChartData.map(item => (
              <StyledLegendItem key={item.name} color={item.color}>
                <LegendPercent>{item.value} %</LegendPercent>
                <LegendLabel>{item.name}</LegendLabel>
              </StyledLegendItem>
            ))}
          </LegendContainer>
        </ChartContainer>
      </Section>

      {/* Team Budget and Expense */}
      <Section>
        <SectionTitle>
          {t("dashboard.finance.teamBudgetAndExpense")}
        </SectionTitle>
        <DataGrid
          rows={
            teamBudgetQuery.data?.data?.map(item => ({
              name: item.team_name,
              totalBudget: item.total_budget,
              totalExpense: item.total_expense
            })) || []
          }
          rowCount={teamBudgetQuery.data?.total || 0}
          loading={teamBudgetQuery.isLoading}
          columns={[
            {
              field: "name",
              headerName: t("dashboard.finance.name"),
              flex: 1,
              minWidth: 200
            },
            {
              field: "totalBudget",
              headerName: t("dashboard.finance.totalBudget"),
              flex: 1,
              minWidth: 150,
              valueFormatter: params => `ETB ${params.value.toLocaleString()}`
            },
            {
              field: "totalExpense",
              headerName: t("dashboard.finance.totalExpense"),
              flex: 1,
              minWidth: 150,
              valueFormatter: params => `ETB ${params.value.toLocaleString()}`
            }
          ]}
          pagination
          rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
          density="comfortable"
          getRowId={row => row.name}
          autoHeight
          disableRowSelectionOnClick
          disableColumnMenu
          paginationMode="server"
          page={page - 1}
          pageSize={pageSize}
          onPageChange={newPage => setPage(newPage + 1)}
          onPageSizeChange={newPageSize => setPageSize(newPageSize)}
          localeText={{
            noRowsLabel: t("teams.teamProfile.NoRows"),
            MuiTablePagination: {
              labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
            }
          }}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#7FA457",
              color: "#000000",
              borderRadius: "8px 8px 0 0",
              fontWeight: 700
            }
          }}
        />
      </Section>
    </Container>
  );
};
